import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { getPtBrPdfToolBySlug, relatedPtBrPdfTools } from "@/lib/pt-br-pdf-catalog";
import { getPtBrPdfToolContent } from "@/lib/pt-br-pdf-content";
import { getEsPdfSlugForEnSlug } from "@/lib/es-pdf-catalog";
import { getDePdfSlugForEnSlug } from "@/lib/de-pdf-catalog";
import { SiteFooter, SiteHeader } from "@/components/tools/ToolShell";
import {
  ArrowLeft,
  BookOpen,
  Sparkles,
  CheckCircle2,
  HelpCircle,
  ShieldCheck,
  FileText,
  ArrowRight,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

export const Route = createFileRoute("/pt-br/pdf-tools/$slug")({
  loader: ({ params }) => {
    const pdfTool = getPtBrPdfToolBySlug(params.slug);
    if (pdfTool && pdfTool.slug !== params.slug) {
      throw redirect({ to: "/pt-br/pdf-tools/$slug", params: { slug: pdfTool.slug }, statusCode: 301 });
    }
    return { pdfTool: pdfTool || null, slug: params.slug };
  },
  head: ({ params }) => {
    const tool = getPtBrPdfToolBySlug(params.slug);
    if (!tool) return { meta: [{ title: "Ferramenta PDF — Picamini.online" }] };

    const content = getPtBrPdfToolContent(tool.slug, tool.name);
    const canonical = `https://picamini.online/pt-br/pdf-tools/${tool.slug}`;
    const enUrl = `https://picamini.online/pdf-tools/${tool.enSlug}`;
    const esSlug = getEsPdfSlugForEnSlug(tool.enSlug);
    const esUrl = `https://picamini.online/es/pdf-tools/${esSlug}`;
    const deSlug = getDePdfSlugForEnSlug(tool.enSlug);
    const deUrl = `https://picamini.online/de/pdf-tools/${deSlug}`;

    const webAppSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": `${tool.name} - PDF Toolkit Pro`,
      "url": canonical,
      "description": content.seoDescription,
      "applicationCategory": "UtilitiesApplication",
      "operatingSystem": "All",
      "browserRequirements": "Requer JavaScript. Requer HTML5.",
      "inLanguage": "pt-BR",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
      },
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Início",
          "item": "https://picamini.online/pt-br/",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Ferramentas PDF",
          "item": "https://picamini.online/pt-br/pdf-tools",
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": tool.name,
          "item": canonical,
        },
      ],
    };

    const faqSchema = content.faq && content.faq.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": content.faq.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    } : null;

    return {
      meta: [
        { title: content.seoTitle || `${tool.name} — Picamini.online` },
        { name: "description", content: content.seoDescription },
        {
          name: "keywords",
          content: tool.keywords?.join(", ") || `${tool.name.toLowerCase()}, ferramentas pdf gratis, picamini`,
        },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.seoTitle },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonical },
        { property: "og:locale", content: "pt_BR" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1" },
      ],
      links: [
        { rel: "canonical", href: canonical },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "es", href: esUrl },
        { rel: "alternate", hrefLang: "de", href: deUrl },
        { rel: "alternate", hrefLang: "pt-BR", href: canonical },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(webAppSchema),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(breadcrumbSchema),
        },
        ...(faqSchema ? [{
          type: "application/ld+json",
          children: JSON.stringify(faqSchema),
        }] : []),
      ],
    };
  },
  component: PtBrPdfToolPageComponent,
});

function PtBrPdfToolPageComponent() {
  const { pdfTool, slug } = Route.useLoaderData();
  const activeTool = pdfTool || getPtBrPdfToolBySlug(slug);

  const [iframeHeight, setIframeHeight] = useState("720px");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    let observer: MutationObserver | null = null;
    let lastHeight = 0;

    const updateHeight = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        try {
          const doc = iframeRef.current.contentWindow.document;
          const container = doc.body;
          if (container) {
            const h = Math.ceil(container.scrollHeight || container.getBoundingClientRect().height);
            if (h > 200 && Math.abs(h - lastHeight) > 10) {
              lastHeight = h;
              setIframeHeight(`${h + 60}px`);
            }
          }
        } catch {
          // Fallback height used if frame cross-origin
        }
      }
    };

    const attachListeners = () => {
      if (iframeRef.current && iframeRef.current.contentWindow) {
        try {
          const doc = iframeRef.current.contentWindow.document;
          if (doc && doc.body) {
            updateHeight();

            if (!observer) {
              observer = new MutationObserver(updateHeight);
              observer.observe(doc.body, {
                childList: true,
                subtree: true,
                attributes: true,
                characterData: true,
              });
            }

            doc.addEventListener("change", updateHeight);
            doc.addEventListener("input", updateHeight);
            doc.addEventListener("click", () => setTimeout(updateHeight, 150));
            doc.addEventListener("drop", () => setTimeout(updateHeight, 300));
          }
        } catch {
          // Fallback
        }
      }
    };

    const t1 = setTimeout(attachListeners, 200);
    const t2 = setTimeout(attachListeners, 600);
    const t3 = setTimeout(attachListeners, 1200);
    const interval = setInterval(updateHeight, 1500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearInterval(interval);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [slug]);

  if (!activeTool) {
    return (
      <div className="min-h-screen bg-background flex flex-col font-sans">
        <SiteHeader lang="pt-BR" />
        <main className="flex-1 py-20 text-center space-y-4">
          <h2 className="text-xl font-bold text-foreground">Ferramenta PDF não encontrada</h2>
          <Link to="/pt-br/pdf-tools" className="inline-block text-sm font-bold text-primary hover:underline">
            ← Voltar para a Central de Ferramentas PDF
          </Link>
        </main>
        <SiteFooter lang="pt-BR" />
      </div>
    );
  }

  const content = getPtBrPdfToolContent(activeTool.slug, activeTool.name);
  const enSlug = activeTool.enSlug;
  const related = relatedPtBrPdfTools(activeTool.slug);

  const toolHtmlMap: Record<string, string> = {
    "merge-pdf": "merge.html",
    "split-pdf": "split.html",
    "organize-pdf": "organize.html",
    "rotate-pdf": "rotate.html",
    "extract-pages": "extract-pages.html",
    "remove-pages": "remove-pages.html",
    "edit-pdf": "edit-pdf.html",
    "page-numbers": "page-numbers.html",
    "watermark-pdf": "watermark.html",
    "compress-pdf": "compress.html",
    "grayscale-pdf": "grayscale.html",
    "repair-pdf": "repair.html",
    "image-to-pdf": "jpg-to-pdf.html",
    "word-to-pdf": "word-to-pdf.html",
    "excel-to-pdf": "excel-to-pdf.html",
    "ppt-to-pdf": "ppt-to-pdf.html",
    "html-to-pdf": "html-to-pdf.html",
    "txt-to-pdf": "txt-to-pdf.html",
    "pdf-to-image": "pdf-to-jpg.html",
    "pdf-to-word": "pdf-to-word.html",
    "pdf-to-excel": "pdf-to-excel.html",
    "pdf-to-text": "pdf-to-text.html",
    "protect-pdf": "protect-pdf.html",
    "unlock-pdf": "unlock-pdf.html",
    "sign-pdf": "sign-pdf.html",
    "ocr-pdf": "ocr.html",
  };

  const htmlFileName = toolHtmlMap[enSlug] || `${enSlug}.html`;
  const iframeSrc = `/pdf-tools/tools/${htmlFileName}?embed=true&lang=pt-BR`;

  return (
    <div className="min-h-screen bg-background flex flex-col font-sans">
      <SiteHeader lang="pt-BR" />

      <main className="flex-1 bg-[#f8fafc]">
        {/* SEO Header Section */}
        <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 pt-6 pb-4">
          <nav className="no-print text-sm">
            <Link
              to="/pt-br/pdf-tools"
              className="inline-flex items-center gap-1.5 text-muted-foreground transition-colors hover:text-foreground font-semibold"
            >
              <ArrowLeft className="size-4" /> Central de Ferramentas PDF
            </Link>
          </nav>

          <div className="no-print pt-3 pb-2">
            <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-primary">
              PDF Toolkit Pro — {activeTool.category}
            </p>
            <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl tracking-tight text-foreground">
              {content.h1Title || `${activeTool.name} Online Grátis — 100% no seu Navegador`}
            </h1>
            <p className="mt-2.5 max-w-4xl text-base text-muted-foreground leading-relaxed">
              {content.intro}
            </p>
          </div>
        </div>

        {/* Interactive PDF Tool Workspace (Iframe) */}
        <div className="w-full mx-auto">
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title={activeTool.name}
            className="w-full border-0 transition-all duration-300 bg-transparent"
            style={{ height: iframeHeight, overflow: "hidden" }}
            allow="clipboard-read; clipboard-write"
            referrerPolicy="strict-origin-when-cross-origin"
          />
        </div>

        {/* Native Brazilian Rich SEO Content Below Tool */}
        <div className="no-print mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-12 space-y-10">

          {/* 1. What is section */}
          {content.whatIs && content.whatIs.length > 0 && (
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-4">
                <BookOpen className="size-5 text-primary" /> O que é {activeTool.name}?
              </h2>
              <div className="space-y-4 text-muted-foreground text-sm sm:text-base leading-relaxed">
                {content.whatIs.map((p, idx) => (
                  <p key={idx}>{p}</p>
                ))}
              </div>
            </section>
          )}

          {/* 2. How to Use & Key Features */}
          <div className="grid gap-6 md:grid-cols-2">
            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <Sparkles className="size-5 text-primary" /> Como usar {activeTool.name}
              </h2>
              <ol className="space-y-3 text-sm sm:text-base text-muted-foreground">
                {content.howTo.map((step, i) => (
                  <li key={step} className="flex gap-3">
                    <span className="grid size-6 shrink-0 place-items-center rounded-full bg-primary/10 text-primary font-bold text-xs">
                      {i + 1}
                    </span>
                    <span className="leading-snug">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
              <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
                <CheckCircle2 className="size-5 text-primary" /> Recursos principais
              </h2>
              <ul className="space-y-3 text-sm sm:text-base text-muted-foreground">
                {content.features.map((f) => (
                  <li key={f} className="flex gap-2.5 items-start">
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                    <span className="leading-snug">{f}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* 3. Security Guarantee Box */}
          <section className="surface-card p-6 sm:p-8 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 space-y-3">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <ShieldCheck className="size-6 text-emerald-600 dark:text-emerald-400" />
              Garantia de Privacidade Absoluta (100% Processamento Local)
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Seus arquivos e imagens processados com a ferramenta <strong>{activeTool.name}</strong> nunca são enviados para servidores externos. Toda a mágica acontece na memória local do seu próprio dispositivo.
            </p>
          </section>

          {/* 4. FAQ Section */}
          <section className="surface-card p-6 sm:p-8 rounded-2xl bg-card border">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2 mb-4">
              <HelpCircle className="size-5 text-primary" /> Perguntas Frecuentes (FAQ)
            </h2>
            <dl className="space-y-4 divide-y divide-border/60">
              {content.faq.map((item, idx) => (
                <div key={item.q} className={idx > 0 ? "pt-4" : ""}>
                  <dt className="font-bold text-sm sm:text-base text-foreground">{item.q}</dt>
                  <dd className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">{item.a}</dd>
                </div>
              ))}
            </dl>
          </section>

          {/* 5. Related PDF Tools Grid */}
          <section className="pt-4">
            <h2 className="text-xl font-bold text-foreground mb-4">Outras Ferramentas PDF Relacionadas</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/pt-br/pdf-tools/$slug"
                  params={{ slug: r.slug }}
                  className="surface-card p-4 rounded-xl border bg-card transition-all hover:-translate-y-0.5 hover:shadow-md space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <FileText className="size-4 text-primary" />
                    <p className="font-bold text-sm text-foreground">{r.name}</p>
                  </div>
                  <p className="text-xs text-muted-foreground line-clamp-2">{r.tagline}</p>
                </Link>
              ))}
            </div>
          </section>

        </div>
      </main>

      <SiteFooter lang="pt-BR" />
    </div>
  );
}
